/**
 * This component supports 3 display modes:
 * - button: a button w/ a small popover that shows the current usage (SM size)
 * - inline: a small inline display that shows the current usage and a progress bar (MD size)
 * - detailed: a detailed view that shows the current usage, quota, and a progress bar (LG size)
 */

import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { CreditUsage, CreditUsageService } from './credit-usage-service.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { AlertType } from '../alert/alert.component';
import { HostnameService } from 'src/app/hostname-router-service';
import { CreditTypeBalance, foldCreditBySource } from 'src/app/shared/utils/credit-sources';

@Component({
    selector: 'app-credit-usage-indicator',
    templateUrl: './credit-usage-indicator.component.html',
    styleUrls: ['./credit-usage-indicator.component.scss'],
    standalone: false
})
export class CreditUsageIndicatorComponent implements OnInit, OnDestroy {
  @Input() hideText: boolean = false;
  @Input() hideProgressBar: boolean = false;
  @Input() transparentBackground: boolean = false;
  @Input() displayMode: 'button' | 'inline' | 'detailed' = 'button';
  @Input() pendingRecords: number = 0;

  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

  orgUsagePolling: Subscription;
  creditUsage: CreditUsage | null = null;
  isDetailCollapsed: boolean = false; // for detailed mode
  // Credit split by type: the settings-configured monthly allowance (in hand
  // immediately) plus the remaining one-time/purchased/etc. balances from the
  // balance endpoint, built when the breakdown is actually shown (popover
  // open / detailed mode).
  creditBreakdown: CreditTypeBalance[] | null = null;
  // The one-time balances are still being fetched / failed to fetch; the
  // monthly row stays visible either way.
  breakdownLoading: boolean = false;
  breakdownError: boolean = false;
  protected AlertType = AlertType;

  get pendingPercentage(): number {
    // Nullish check: an explicit zero quota is a real enforced limit, so any
    // pending records fill whatever the usage bar hasn't (all of them exceed).
    const quota = this.creditUsage?.peopleQuota;
    if (!this.pendingRecords || quota == null) return 0;
    const usagePct = Math.min(this.creditUsage?.peopleUsagePercentage || 0, 100);
    if (quota === 0) {
      return 100 - usagePct;
    }
    return Math.max(0, Math.min((this.pendingRecords / quota) * 100, 100 - usagePct));
  }

  get clampedUsagePercentage(): number {
    return Math.min(Math.max(this.creditUsage?.peopleUsagePercentage || 0, 0), 100);
  }

  get wouldExceedQuota(): boolean {
    if (this.creditUsage?.peopleQuota == null) return false;
    return (this.creditUsage?.peopleUsagePercentage || 0) + this.pendingPercentage >= 100;
  }

  // The "monthly credits renew" notice only applies to orgs that actually
  // have a recurring monthly allowance configured in their org settings
  // (which keeps it showing when the allowance is fully spent mid-cycle).
  get showRenewalNotice(): boolean {
    return (this.creditUsage?.monthlyCredit || 0) > 0;
  }

  constructor(
    private router: Router,
    private sseService: CreditUsageService,
    private authService: AuthService
  ) {
    this.orgUsagePolling = this.sseService.creditUsage$.subscribe((creditUsage) => {
      this.creditUsage = creditUsage;
    });
  }

  ngOnInit() {
    // Detailed mode renders the breakdown inline (no popover to trigger it).
    if (this.displayMode === 'detailed') {
      this.loadBreakdown();
    }
  }

  // Build the breakdown: the monthly allowance comes from org settings and is
  // already in hand, so show it immediately; only the remaining one-time and
  // purchased balances need fetching from the balance endpoint. Called
  // whenever the breakdown becomes visible so it reflects the current cycle.
  loadBreakdown() {
    this.creditBreakdown = foldCreditBySource([], this.creditUsage?.monthlyCredit || 0);
    this.breakdownLoading = true;
    this.breakdownError = false;
    this.sseService.getPeopleCreditBreakdown().subscribe({
      next: (bySource) => {
        this.breakdownLoading = false;
        if (bySource === null) {
          // Keep the settings-sourced monthly row; only the one-time fetch failed.
          this.breakdownError = true;
          return;
        }
        this.creditBreakdown = foldCreditBySource(bySource, this.creditUsage?.monthlyCredit || 0);
      },
      error: () => {
        this.breakdownLoading = false;
        this.breakdownError = true;
      },
    });
  }

  navigateToSettings() {
    const orgId = this.authService.getSelectedOrgIdValue;
    this.router.navigate([`/${orgId}/settings`]);
  }

  goToBilling() {
    if (HostnameService.isWorkforceExperience()) {
      this.router.navigate(['/billing']);
    } else {
      this.router.navigate([`/${this.authService.getSelectedOrgIdValue}/settings`]);
    }
  }

  // by default, popover (mat-menu) closes on 'Enter' key, but let's add additional support for escape key so user isn't stuck in the menu
  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.menuTrigger.menuOpen) {
      this.closeMenu();
    }
  }

  closeMenu() {
    if (this.menuTrigger && this.menuTrigger.menuOpen) {
      this.menuTrigger.closeMenu();
    }
  }

  // toggle collapse state for detailed mode
  toggleDetailCollapse() {
    this.isDetailCollapsed = !this.isDetailCollapsed;
    if (!this.isDetailCollapsed) {
      // Re-expanding: refresh the by-type balances.
      this.loadBreakdown();
    }
  }

  ngOnDestroy() {
    this.orgUsagePolling.unsubscribe();
  }
}
